import { useState } from 'react';
import { Archive, ArchiveRestore, Bell, BellOff, BellRing, MoreVertical, Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { notificationsFromRow } from '../../../lib/group-notifications';
import { GroupNotificationsDialog } from '../group-chat/GroupNotifications';
import { useTranslation } from '../../../lib/i18n';
import { useGroupChatEnabled } from '../../../lib/group-chat-flag';
import { parseViewerState } from '../../../lib/group-chat';
import { setGroupViewerState } from '../../../lib/group-chat-api';
import type { Conversation } from '../../../lib/conversations';
import type { ConversationActionsFlow } from '../../../lib/use-conversation-actions';
import { OverflowMenu } from '../ui';

/**
 * The 3-dot menu on a conversation row: Pin/Unpin, Rename and Delete.
 *
 * One component for every list that shows conversations — the wide rail's Chat History, the mobile drawer,
 * the search dialog — so the actions, their order, their glyphs and their labels are identical wherever a
 * thread appears. A row menu that offers different things on different screens is the drift this exists to
 * prevent.
 *
 * The pin item is a TOGGLE that names the outcome ("Pin" / "Unpin") rather than the state, so the label
 * always describes what pressing it will do. Delete sits last and alone in the destructive skin, so the one
 * item that removes something is never adjacent to the one that is pressed most.
 */
export function ConversationRowMenu({
  conv,
  title,
  flow,
  triggerSize,
  triggerClassName,
}: {
  conv: Conversation;
  title: string;
  flow: ConversationActionsFlow;
  /** The new desktop rail draws its row controls at 26 (Figma `Sidebar-Menu`); every other list keeps the default. */
  triggerSize?: 'tiny' | 'small';
  triggerClassName?: string;
}) {
  const { t } = useTranslation();
  const pinned = Boolean(conv.pinned);

  // A row with no server id cannot be pinned or deleted — both writes address the conversation by id. Better
  // to offer no menu than a menu whose every item is a silent no-op. This also narrows `conv.id` to a string
  // for the calls below.
  const groupChatEnabled = useGroupChatEnabled();
  const [notifyOpen, setNotifyOpen] = useState(false);
  const id = conv.id;
  if (!id) return null;

  // The caller's own notification mode for a live group (owner and members alike): a quick "Notifications…" entry
  // opening the same picker as the group info's row. Absent from an older service → no entry.
  const notifications = groupChatEnabled ? notificationsFromRow(conv) : null;
  const notifyAction = notifications
    ? [{ key: 'notifications', label: t('chat.group.notify.open'), icon: <BellRing size={16} aria-hidden />, onSelect: () => setNotifyOpen(true) }]
    : [];
  const notifyDialog = notifyOpen && notifications ? (
    <GroupNotificationsDialog conversationId={id} notifications={notifications} onClose={() => setNotifyOpen(false)} />
  ) : null;

  // A group the viewer JOINED (Phase 3b, behind `symphonyGroupChat`): the row is the member's own view of someone
  // else's chat, so its menu is the member's own pin / mute / archive (`viewer-state`) — nobody but the owner can
  // rename or delete the chat itself. The owner's row has no `viewerState` and keeps the menu below.
  const viewerState = groupChatEnabled ? parseViewerState(conv.viewerState) : null;
  if (viewerState) {
    return (
      <>
      <OverflowMenu
        triggerSize={triggerSize}
        triggerClassName={triggerClassName}
        triggerIcon={triggerSize === 'tiny' ? <MoreVertical size={16} aria-hidden /> : undefined}
        ariaLabel={t('conversations.rowMenu.label', { title })}
        actions={[
          {
            key: 'pin',
            label: viewerState.pinned ? t('conversations.unpin') : t('conversations.pin'),
            icon: viewerState.pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
            onSelect: () => void setGroupViewerState(id, { pinned: !viewerState.pinned }),
          },
          {
            key: 'mute',
            label: viewerState.muted ? t('chat.group.unmute') : t('chat.group.mute'),
            icon: viewerState.muted ? <Bell size={16} aria-hidden /> : <BellOff size={16} aria-hidden />,
            onSelect: () => void setGroupViewerState(id, { muted: !viewerState.muted }),
          },
          ...notifyAction,
          {
            key: 'archive',
            label: viewerState.archived ? t('chat.group.unarchive') : t('chat.group.archive'),
            icon: viewerState.archived ? <ArchiveRestore size={16} aria-hidden /> : <Archive size={16} aria-hidden />,
            onSelect: () => void setGroupViewerState(id, { archived: !viewerState.archived }),
          },
        ]}
      />
      {notifyDialog}
      </>
    );
  }

  return (
    <>
    <OverflowMenu
      ariaLabel={t('conversations.rowMenu.label', { title })}
      triggerSize={triggerSize}
      triggerClassName={triggerClassName}
      triggerIcon={triggerSize === 'tiny' ? <MoreVertical size={16} aria-hidden /> : undefined}
      actions={[
        {
          key: 'pin',
          label: pinned ? t('conversations.unpin') : t('conversations.pin'),
          icon: pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
          onSelect: () => void flow.togglePin({ id, pinned }),
        },
        {
          key: 'rename',
          label: t('conversations.rename.action'),
          icon: <Pencil size={16} aria-hidden />,
          onSelect: () => flow.requestRename({ id, title }),
        },
        ...notifyAction,
        {
          key: 'delete',
          label: t('conversations.delete.action'),
          icon: <Trash2 size={16} aria-hidden />,
          destructive: true,
          onSelect: () => flow.requestDelete({ id, title }),
        },
      ]}
    />
    {notifyDialog}
    </>
  );
}
